/**
 * Feed Compose page — S010 shell.
 *
 * Route: /feed/compose
 * Auth required: yes (uses auth store)
 * Sponsor required: no
 *
 * CORRECTION01 changes:
 * - R4: Composes production dependencies (createPostAdapter + registrationKind)
 * - R11: Success state does NOT navigate to /feed (no route exists)
 * - Success state holds the user on the composer screen
 *
 * Wires the Elm-ish feed composer program to the router.
 * Handles: program lifecycle, navigation on cancel.
 *
 * Note: RoutePaths.FEED (/feed) has no registered route in the application.
 * Cancel navigates to '/' (home). Success state holds on the composer.
 * Navigation to feed requires a successor ACT with a real feed route.
 */
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import { useFeedComposerProgram } from '@/features/feed-composer/useFeedComposerProgram';
import { FeedComposerView } from '@/features/feed-composer/view';
import { createPostAdapter } from '@/api/feedPosts';
import { useAuthStore } from '@/store/authStore';
import { getSponsorProfile, getFriendProfile } from '@/api/profiles';
import { buildAuthorSummary, type AuthorSummary } from '@/features/feed-composer/model';
import type { RegistrationKind } from '@/store/authStore';
import type { ComposerDependencies } from '@/features/feed-composer/effects';

// =============================================================================
// Production composer dependencies (composition root)
// =============================================================================
//
// ACT-INDEEP-PRODUCTION-FEED-COMPOSER-API-WIRING01-CORRECTION01:
// Extracted from the page body into a named, exported factory so that
// the production composition root can be unit-tested directly.
//
// The factory MUST wire the real `createPostAdapter` for `createPost`.
// A regression test in `feedPosts.test.ts` / a sibling composition test
// asserts `buildProductionComposerDependencies(...).createPost ===
// createPostAdapter` by identity. If a future change replaces this with
// a stub (e.g., reintroducing the unavailable-only stub), the test
// fails loudly. This is the only way to prove "the page actually receives
// the real adapter" without spinning up a browser.
//
// The factory is pure w.r.t. `registrationKind`: it does not read the
// auth store directly, so unit tests can pin the input.

// CORRECTION02: loadAuthor adapter — exactly-one-authority (R6)
// Calls sponsor OR friend profile, never both. Returns null for unclassified.
function buildLoadAuthorAdapter(registrationKind: RegistrationKind) {
  return {
    async load(signal: AbortSignal): Promise<AuthorSummary | null> {
      if (signal.aborted) return null;
      switch (registrationKind) {
        case 'sponsor': {
          const profile = await getSponsorProfile();
          if (signal.aborted) return null;
          if (!profile) return null;
          return buildAuthorSummary({
            displayName: profile.display_name ?? 'User',
            age: profile.age && profile.age > 0 ? profile.age : null,
            location: profile.city_or_region ?? null,
          });
        }
        case 'friend': {
          const profile = await getFriendProfile();
          if (signal.aborted) return null;
          if (!profile) return null;
          return buildAuthorSummary({
            displayName: profile.display_name ?? 'User',
            age: profile.age && profile.age > 0 ? profile.age : null,
            location: profile.city_or_region ?? null,
          });
        }
        case 'unclassified':
        case '':
        default:
          return null;
      }
    },
  };
}

/**
 * Build the production composer dependency object.
 *
 * ACT-INDEEP-PRODUCTION-FEED-COMPOSER-API-WIRING01-CORRECTION01: The
 * previous in-page useMemo closed over createPostAdapter without an
 * identity-preserving export. This factory is the SINGLE composition
 * root for the page's production wiring — every caller (the page,
 * tests) goes through it. A regression test asserts that
 * `deps.createPost === createPostAdapter` by reference.
 *
 * NOTE: `createPost: createPostAdapter` MUST NOT be replaced by a stub.
 * The earlier "returns 'unavailable' unconditionally" stub was a
 * placeholder; replacing it would silently re-break /feed/compose.
 */
export function buildProductionComposerDependencies(
  registrationKind: RegistrationKind
): ComposerDependencies {
  return {
    createPost: createPostAdapter,
    loadAuthor: buildLoadAuthorAdapter(registrationKind),
  };
}

/**
 * Feed compose page.
 *
 * Composition root for the feed composer program.
 * Owns the concrete adapters and wires them to the program.
 *
 * CORRECTION01: Success state holds the user on the composer screen.
 * RoutePaths.FEED has no registered route — see R11.
 */
export function FeedComposePage(): JSX.Element {
  const navigate = useNavigate();

  // Get registration kind from auth store (R10)
  const registrationKind = useAuthStore((state) => state.registrationKind);

  // Stable dependencies — memoized by registrationKind (P0: dependency identity)
  const dependencies = useMemo<ComposerDependencies>(
    () => buildProductionComposerDependencies(registrationKind),
    [registrationKind]
  );

  const {
    model,
    start,
    changeContent,
    toggleAnonymous,
    publish,
    retry,
    cancel,
  } = useFeedComposerProgram({
    dependencies,
  });

  // Start the program on mount
  useEffect(() => {
    start();
  }, [start]);

  // CORRECTION01: Success state holds on composer — no route exists for /feed
  // The user sees a success confirmation. Navigation to feed requires a real feed route.
  // Success state is non-interactive (publish disabled, textarea disabled).

  // Cancel navigates back to home (R11: RoutePaths.FEED has no registered route)
  const handleCancel = () => {
    cancel();
    navigate(RoutePaths.HOME, { replace: true });
  };

  return (
    <div className="page">
      <FeedComposerView
        model={model}
        onContentChange={changeContent}
        onAnonymousToggle={toggleAnonymous}
        onPublish={publish}
        onRetry={retry}
        onCancel={handleCancel}
      />
    </div>
  );
}
